/**
 * Timeline Showcase — 5 premium design-varianter
 * Ingen motorcykel. Ren, hurtig, CSS-animeret parallax.
 */
import { useEffect, useRef, useState } from 'react';

// ─── GPS hjælpefunktioner ──────────────────────────────────────────────────
function gY(x, W, H) {
  const t = x / Math.max(1, W);
  return Math.max(H * 0.62, Math.min(H * 0.86,
    H * 0.76
    + H * 0.025 * Math.sin(t * Math.PI * 1.6 + 0.3)
    + H * 0.014 * Math.sin(t * Math.PI * 3.1 + 0.8)
    + H * 0.008 * Math.sin(t * Math.PI * 5.4 - 0.5)
    - H * 0.010 * Math.sin(t * Math.PI)
  ));
}
function buildGPS(W, H, x0 = 0, x1 = W) {
  let d = '';
  for (let x = x0; x <= x1; x += 2) {
    const y = gY(x, W, H).toFixed(1);
    d += d ? ` L${x} ${y}` : `M${x} ${y}`;
  }
  return d;
}

// ─── Demo data ─────────────────────────────────────────────────────────────
const DEMO = [
  { progress: 0.12, type: 'photo'   },
  { progress: 0.30, type: 'video'   },
  { progress: 0.50, type: 'club'    },
  { progress: 0.68, type: 'pitstop' },
  { progress: 0.85, type: 'photo'   },
  { progress: 1.00, type: 'flag'    },
];

const TYPE_COLOR = {
  photo:   '#2DFF88',
  video:   '#44AAFF',
  club:    '#FF8820',
  pitstop: '#FFB020',
  flag:    '#FFFFFF',
};

const TYPE_LABEL = {
  photo: 'FOTO', video: 'VIDEO', club: 'KLUB', pitstop: 'STOP', flag: 'MÅL',
};

// ─── CSS (animationer, ingen React-state parallax) ─────────────────────────
const CSS = `
  @keyframes sc-pan { from { background-position-x: 0% } to { background-position-x: -30% } }
  @keyframes sc-pan-mid { from { background-position-x: 0% } to { background-position-x: -50% } }
  @keyframes sc-glow { 0%,100%{opacity:.7}50%{opacity:1} }
  @keyframes sc-pulse { 0%,100%{transform:scale(1)}50%{transform:scale(1.3)} }
  @keyframes sc-road { from{stroke-dashoffset:300}to{stroke-dashoffset:0} }
  .sc-wrap { font-family: system-ui, -apple-system, sans-serif; background:#0a0f0a; min-height:100vh; padding:0 0 60px; }
  .sc-hero { padding: 48px 20px 32px; text-align:center; }
  .sc-hero h1 { font-size:28px; font-weight:900; color:#fff; letter-spacing:.04em; margin:0 0 8px; }
  .sc-hero p  { font-size:14px; color:rgba(255,255,255,0.45); margin:0; }
  .sc-card { margin: 0 16px 32px; border-radius:16px; overflow:hidden; background:#111; }
  .sc-card-head { padding:14px 18px 10px; display:flex; justify-content:space-between; align-items:center; }
  .sc-card-name { font-size:13px; font-weight:800; letter-spacing:.10em; color:#fff; }
  .sc-card-sub  { font-size:11px; color:rgba(255,255,255,0.40); margin-top:2px; }
  .sc-pick { background:#2DFF88; color:#000; border:none; border-radius:20px; font-size:11px; font-weight:900; padding:6px 14px; letter-spacing:.06em; cursor:pointer; }
  .sc-strip { position:relative; height:110px; overflow:hidden; }
`;

// ═══════════════════════════════════════════════════════════════════════════
// DESIGN 1 — PANORAMA  (lys natur, elegante badges)
// ═══════════════════════════════════════════════════════════════════════════
function Design1({ W }) {
  const H = 110;
  const gps = buildGPS(W, H);
  const uid = 'd1';
  return (
    <div className="sc-strip">
      {/* Baggrundsfoto — lyst */}
      <div style={{
        position:'absolute', inset:0,
        backgroundImage:`url(/tl-bg-day.png)`,
        backgroundSize:`${W * 1.6}px 100%`,
        backgroundRepeat:'no-repeat',
        filter:'brightness(1.7) saturate(1.25) contrast(1.05)',
        animation:'sc-pan 40s linear infinite',
      }}/>
      {/* Bund-gradient for GPS-synlighed */}
      <div style={{ position:'absolute', inset:0, background:'linear-gradient(to top, rgba(0,0,0,0.30) 0%, transparent 55%)' }}/>

      <svg width={W} height={H} style={{ position:'absolute', inset:0 }} overflow="visible">
        <defs>
          <filter id={`${uid}-g`}>
            <feGaussianBlur stdDeviation="3" result="blur"/>
            <feMerge><feMergeNode in="blur"/><feMergeNode in="SourceGraphic"/></feMerge>
          </filter>
        </defs>
        {/* Fremtidig rute */}
        <path d={gps} stroke="rgba(255,255,255,0.22)" strokeWidth="2" fill="none" strokeLinecap="round"/>
        {/* GPS core */}
        <path d={gps} stroke="#2DFF88" strokeWidth="2.5" fill="none" strokeLinecap="round"
          filter={`url(#${uid}-g)`} opacity="0.9"/>
        {/* Pins */}
        {DEMO.map((ms, i) => {
          if (ms.type === 'flag') return null;
          const mx = Math.round(ms.progress * W);
          const my = gY(mx, W, H);
          const col = TYPE_COLOR[ms.type];
          const stemH = 38 + (i % 4) * 14;
          const py = my - stemH - 22;
          return (
            <g key={i}>
              <line x1={mx} y1={my} x2={mx} y2={py + 22}
                stroke={col} strokeWidth="1.4" opacity="0.7"/>
              <rect x={mx - 16} y={py} width="32" height="20" rx="6"
                fill="rgba(8,12,8,0.85)" stroke={col} strokeWidth="1.5"/>
              <text x={mx} y={py + 13.5} textAnchor="middle"
                fill={col} fontSize="7.5" fontWeight="800" letterSpacing="0.06em"
                fontFamily="system-ui">{TYPE_LABEL[ms.type]}</text>
              <circle cx={mx} cy={my} r="3.5" fill={col}/>
            </g>
          );
        })}
        {/* Målflag */}
        {(() => {
          const fx = Math.round(0.97 * W);
          const fy = gY(fx, W, H);
          return (
            <g>
              <line x1={fx} y1={fy} x2={fx} y2={fy - 24} stroke="#fff" strokeWidth="1.5" strokeLinecap="round"/>
              {[0,1,2,3].map(c => [0,1,2].map(r => (
                <rect key={`${c}-${r}`} x={fx + c*5} y={fy - 24 + r*5} width="5" height="5"
                  fill={(c+r)%2===0 ? 'rgba(255,255,255,0.95)' : 'rgba(10,10,10,0.95)'}/>
              )))}
              <circle cx={fx} cy={fy} r="3" fill="#fff"/>
            </g>
          );
        })()}
      </svg>
      {/* Branding */}
      <div style={{ position:'absolute', bottom:7, right:10, fontSize:10, fontWeight:900,
        color:'#fff', letterSpacing:'.12em', textShadow:'0 1px 6px rgba(0,0,0,0.9)' }}>
        <span>BARE</span><span style={{ color:'#2DFF88' }}>MC</span>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// DESIGN 2 — FILM NOIR  (filmstrimmel, amber GPS)
// ═══════════════════════════════════════════════════════════════════════════
function Design2({ W }) {
  const H = 110;
  const gps = buildGPS(W, H);
  const uid = 'd2';
  const holes = Math.floor(W / 32);
  return (
    <div className="sc-strip" style={{ background:'#080808' }}>
      {/* Midterfelt — lys baggrund bag GPS */}
      <div style={{
        position:'absolute', left:0, right:0, top:14, bottom:14,
        background:'linear-gradient(to bottom, #1a1005 0%, #120d02 100%)',
      }}/>
      {/* Top sprocket-huller */}
      <div style={{ position:'absolute', top:0, left:0, right:0, height:14,
        background:'#050505', display:'flex', alignItems:'center', padding:'0 6px', gap:0 }}>
        {Array.from({length:holes}).map((_,i) => (
          <div key={i} style={{ width:10, height:8, borderRadius:2,
            background:'#1a1a1a', marginRight:22, flexShrink:0 }}/>
        ))}
      </div>
      {/* Bund sprocket-huller */}
      <div style={{ position:'absolute', bottom:0, left:0, right:0, height:14,
        background:'#050505', display:'flex', alignItems:'center', padding:'0 6px', gap:0 }}>
        {Array.from({length:holes}).map((_,i) => (
          <div key={i} style={{ width:10, height:8, borderRadius:2,
            background:'#1a1a1a', marginRight:22, flexShrink:0 }}/>
        ))}
      </div>

      <svg width={W} height={H} style={{ position:'absolute', inset:0 }} overflow="visible">
        <defs>
          <filter id={`${uid}-g`}>
            <feGaussianBlur stdDeviation="4" result="blur"/>
            <feMerge><feMergeNode in="blur"/><feMergeNode in="SourceGraphic"/></feMerge>
          </filter>
        </defs>
        {/* Fremtidig rute */}
        <path d={gps} stroke="rgba(180,100,0,0.25)" strokeWidth="2" fill="none"/>
        {/* GPS amber glow */}
        <path d={gps} stroke="#FFB040" strokeWidth="3" fill="none" strokeLinecap="round"
          filter={`url(#${uid}-g)`} opacity="0.85"/>
        {/* Pins */}
        {DEMO.map((ms, i) => {
          if (ms.type === 'flag') return null;
          const mx = Math.round(ms.progress * W);
          const my = gY(mx, W, H);
          const stemH = 30 + (i % 3) * 16;
          const py = my - stemH - 24;
          return (
            <g key={i}>
              <line x1={mx} y1={my} x2={mx} y2={py + 24}
                stroke="#FFB040" strokeWidth="1" strokeDasharray="3 3" opacity="0.6"/>
              <rect x={mx - 14} y={py} width="28" height="22" rx="3"
                fill="#0a0a0a" stroke="#FFB040" strokeWidth="1.5"/>
              {/* Film-frame linjer */}
              <line x1={mx-11} y1={py+2} x2={mx-11} y2={py+20} stroke="#1a1a1a" strokeWidth="2"/>
              <line x1={mx+11} y1={py+2} x2={mx+11} y2={py+20} stroke="#1a1a1a" strokeWidth="2"/>
              <text x={mx} y={py+14} textAnchor="middle"
                fill="#FFB040" fontSize="7" fontWeight="800" letterSpacing=".05em"
                fontFamily="monospace">{TYPE_LABEL[ms.type]}</text>
              <circle cx={mx} cy={my} r="3" fill="#FFB040"/>
            </g>
          );
        })}
        {/* Målflag */}
        {(() => {
          const fx = Math.round(0.97 * W);
          const fy = gY(fx, W, H);
          return (
            <g>
              <line x1={fx} y1={fy} x2={fx} y2={fy - 22} stroke="#FFB040" strokeWidth="1.5" strokeLinecap="round"/>
              {[0,1,2,3].map(c => [0,1,2].map(r => (
                <rect key={`${c}-${r}`} x={fx + c*4} y={fy - 22 + r*4} width="4" height="4"
                  fill={(c+r)%2===0 ? 'rgba(255,176,64,0.9)' : 'rgba(10,8,2,0.95)'}/>
              )))}
            </g>
          );
        })()}
      </svg>
      {/* Branding */}
      <div style={{ position:'absolute', bottom:20, right:10, fontSize:10, fontWeight:900,
        color:'#FFB040', letterSpacing:'.12em', fontFamily:'monospace' }}>BAREMC</div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// DESIGN 3 — HORIZON  (himmel + jord, hvid GPS-linje)
// ═══════════════════════════════════════════════════════════════════════════
function Design3({ W }) {
  const H = 110;
  const gps = buildGPS(W, H);
  const uid = 'd3';
  return (
    <div className="sc-strip">
      {/* Himmel */}
      <div style={{
        position:'absolute', inset:0,
        background:'linear-gradient(to bottom, #87CEEB 0%, #B3E5FC 38%, #C8E6C9 52%, #388E3C 62%, #1B5E20 100%)',
      }}/>
      {/* Sol */}
      <div style={{
        position:'absolute', right:'18%', top:6, width:22, height:22, borderRadius:'50%',
        background:'radial-gradient(circle, #FFF9C4 0%, #FFF176 60%, rgba(255,241,118,0) 100%)',
        boxShadow:'0 0 28px 10px rgba(255,241,118,0.50)',
      }}/>
      {/* Lette skyer */}
      <div style={{ position:'absolute', left:'8%', top:10, width:60, height:14, borderRadius:99,
        background:'rgba(255,255,255,0.72)', filter:'blur(4px)' }}/>
      <div style={{ position:'absolute', left:'28%', top:18, width:40, height:10, borderRadius:99,
        background:'rgba(255,255,255,0.55)', filter:'blur(3px)' }}/>
      <div style={{ position:'absolute', right:'12%', top:8, width:50, height:12, borderRadius:99,
        background:'rgba(255,255,255,0.60)', filter:'blur(3px)' }}/>

      <svg width={W} height={H} style={{ position:'absolute', inset:0 }} overflow="visible">
        <defs>
          <filter id={`${uid}-g`}>
            <feGaussianBlur stdDeviation="3.5" result="blur"/>
            <feMerge><feMergeNode in="blur"/><feMergeNode in="SourceGraphic"/></feMerge>
          </filter>
        </defs>
        {/* GPS: hvid sti over horisonten */}
        <path d={gps} stroke="rgba(255,255,255,0.35)" strokeWidth="3" fill="none" strokeLinecap="round"/>
        <path d={gps} stroke="#ffffff" strokeWidth="2.5" fill="none" strokeLinecap="round"
          filter={`url(#${uid}-g)`} opacity="0.92"/>
        {/* Pins — stiger op mod himlen */}
        {DEMO.map((ms, i) => {
          if (ms.type === 'flag') return null;
          const mx = Math.round(ms.progress * W);
          const my = gY(mx, W, H);
          const col = ms.type === 'club' ? '#FF8820' : ms.type === 'video' ? '#44AAFF' : ms.type === 'pitstop' ? '#FFB020' : '#fff';
          const stemH = 32 + (i % 4) * 10;
          const py = my - stemH - 18;
          return (
            <g key={i}>
              <line x1={mx} y1={my} x2={mx} y2={py + 18}
                stroke="rgba(255,255,255,0.6)" strokeWidth="1.2"/>
              <circle cx={mx} cy={py + 9} r="10"
                fill="rgba(255,255,255,0.18)" stroke="rgba(255,255,255,0.75)" strokeWidth="1.5"/>
              <text x={mx} y={py + 13} textAnchor="middle"
                fill={col} fontSize="6.5" fontWeight="900" letterSpacing=".04em"
                fontFamily="system-ui">{TYPE_LABEL[ms.type]}</text>
              <circle cx={mx} cy={my} r="3" fill="#fff"/>
            </g>
          );
        })}
        {/* Målflag */}
        {(() => {
          const fx = Math.round(0.97 * W);
          const fy = gY(fx, W, H);
          return (
            <g>
              <line x1={fx} y1={fy} x2={fx} y2={fy - 22} stroke="#fff" strokeWidth="1.5" strokeLinecap="round"/>
              {[0,1,2,3].map(c => [0,1,2].map(r => (
                <rect key={`${c}-${r}`} x={fx+c*5} y={fy-22+r*5} width="5" height="5"
                  fill={(c+r)%2===0 ? 'rgba(255,255,255,0.97)' : 'rgba(10,50,15,0.95)'}/>
              )))}
            </g>
          );
        })()}
      </svg>
      {/* Branding */}
      <div style={{ position:'absolute', bottom:7, right:10, fontSize:10, fontWeight:900,
        color:'rgba(255,255,255,0.85)', letterSpacing:'.12em', textShadow:'0 1px 4px rgba(0,0,0,0.5)' }}>
        <span>BARE</span><span style={{ color:'#2DFF88' }}>MC</span>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// DESIGN 4 — ATLAS  (topografisk kort, rød stiplet rute)
// ═══════════════════════════════════════════════════════════════════════════
function Design4({ W }) {
  const H = 110;
  const gps = buildGPS(W, H);
  const uid = 'd4';
  // Byg gradient-fyldt path (under GPS)
  const fillPath = gps + ` L${W} ${H} L0 ${H} Z`;
  return (
    <div className="sc-strip">
      {/* Parchment baggrund */}
      <div style={{
        position:'absolute', inset:0,
        background:'linear-gradient(135deg, #f2e8d0 0%, #ede0c4 50%, #e8d8b8 100%)',
      }}/>
      {/* Gitterlinjer */}
      <svg width={W} height={H} style={{ position:'absolute', inset:0, opacity:0.22 }}>
        {Array.from({length:Math.ceil(W/28)+1}).map((_,i) => (
          <line key={`v${i}`} x1={i*28} y1={0} x2={i*28} y2={H}
            stroke="#8B6914" strokeWidth="0.5"/>
        ))}
        {Array.from({length:Math.ceil(H/18)+1}).map((_,i) => (
          <line key={`h${i}`} x1={0} y1={i*18} x2={W} y2={i*18}
            stroke="#8B6914" strokeWidth="0.5"/>
        ))}
      </svg>
      {/* Terræn-kurver */}
      <svg width={W} height={H} style={{ position:'absolute', inset:0, opacity:0.18 }}>
        {[0.3,0.5,0.7].map((f, i) => {
          const shifted = buildGPS(W, H * 1.4, 0, W).replace(/M(\d+\.?\d*) (\d+\.?\d*)/g, (_, x, y) => `M${x} ${parseFloat(y) - H * f * 0.2}`);
          return <path key={i} d={buildGPS(W, H)} stroke="#8B6914" strokeWidth="0.8" fill="none" transform={`translate(0, ${-H * 0.08 * (i+1)})`}/>;
        })}
      </svg>

      <svg width={W} height={H} style={{ position:'absolute', inset:0 }} overflow="visible">
        <defs>
          <linearGradient id={`${uid}-fill`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="rgba(180,40,0,0.12)"/>
            <stop offset="100%" stopColor="rgba(180,40,0,0)"/>
          </linearGradient>
          <filter id={`${uid}-g`}>
            <feGaussianBlur stdDeviation="2"/>
          </filter>
        </defs>
        {/* Fyld under ruten */}
        <path d={fillPath} fill={`url(#${uid}-fill)`}/>
        {/* Gule GPS-streger */}
        <path d={gps} stroke="rgba(180,40,0,0.18)" strokeWidth="5" fill="none" strokeLinecap="round"/>
        <path d={gps} stroke="#CC2200" strokeWidth="2.2" fill="none" strokeLinecap="round"
          strokeDasharray="10 5"/>
        {/* Pins — kortpin stil */}
        {DEMO.map((ms, i) => {
          if (ms.type === 'flag') return null;
          const mx = Math.round(ms.progress * W);
          const my = gY(mx, W, H);
          const stemH = 28 + (i % 4) * 12;
          const py = my - stemH - 20;
          const num = i + 1;
          return (
            <g key={i}>
              <line x1={mx} y1={my} x2={mx} y2={py + 20}
                stroke="#CC2200" strokeWidth="1" opacity="0.5"/>
              {/* Teardrop pin */}
              <path d={`M${mx},${py+20} C${mx-8},${py+14} ${mx-8},${py+4} ${mx},${py} C${mx+8},${py+4} ${mx+8},${py+14} ${mx},${py+20}Z`}
                fill="#CC2200" opacity="0.88"/>
              <circle cx={mx} cy={py+8} r="5.5" fill="rgba(244,232,208,0.92)"/>
              <text x={mx} y={py+12} textAnchor="middle"
                fill="#CC2200" fontSize="7" fontWeight="900" fontFamily="system-ui">{num}</text>
              <circle cx={mx} cy={my} r="2.5" fill="#CC2200"/>
            </g>
          );
        })}
        {/* Målflag — stjernemarkering */}
        {(() => {
          const fx = Math.round(0.97 * W);
          const fy = gY(fx, W, H);
          return (
            <g>
              <line x1={fx} y1={fy} x2={fx} y2={fy-20} stroke="#CC2200" strokeWidth="1.5"/>
              {[0,1,2,3].map(c => [0,1,2].map(r => (
                <rect key={`${c}-${r}`} x={fx+c*5} y={fy-20+r*5} width="5" height="5"
                  fill={(c+r)%2===0 ? '#CC2200' : 'rgba(244,232,208,0.9)'}/>
              )))}
              <circle cx={fx} cy={fy} r="3" fill="#CC2200"/>
            </g>
          );
        })()}
      </svg>
      {/* Afstandsmarkering */}
      <div style={{ position:'absolute', bottom:7, right:10, fontSize:10, fontWeight:800,
        color:'#8B4513', letterSpacing:'.10em', fontFamily:'Georgia, serif' }}>BAREMC</div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// DESIGN 5 — CYBER ROAD  (nat, neon, hastighed)
// ═══════════════════════════════════════════════════════════════════════════
function Design5({ W }) {
  const H = 110;
  const gps = buildGPS(W, H);
  const uid = 'd5';
  // Vejstriber — midt på strip
  const roadY = Math.round(H * 0.76);
  return (
    <div className="sc-strip" style={{ background:'#030308' }}>
      {/* Stjernebaggrund */}
      <div style={{ position:'absolute', inset:0, overflow:'hidden' }}>
        {[...Array(24)].map((_,i) => (
          <div key={i} style={{
            position:'absolute',
            left:`${(i * 43 % 100)}%`,
            top:`${(i * 31 % 55)}%`,
            width: i%4===0 ? 2 : 1,
            height: i%4===0 ? 2 : 1,
            borderRadius:'50%',
            background:`rgba(255,255,255,${0.2 + (i%5)*0.1})`,
          }}/>
        ))}
      </div>
      {/* Vejbane */}
      <div style={{
        position:'absolute', left:0, right:0,
        top: roadY - 10, height:20,
        background:'rgba(20,20,35,0.85)',
        borderTop:'1px solid rgba(255,255,255,0.08)',
        borderBottom:'1px solid rgba(255,255,255,0.08)',
      }}/>
      {/* Vejstriber (hvide) */}
      <div style={{
        position:'absolute', left:0, right:0,
        top: roadY - 1, height:2,
        background:`repeating-linear-gradient(to right, rgba(255,255,255,0.3) 0px, rgba(255,255,255,0.3) 20px, transparent 20px, transparent 40px)`,
      }}/>

      <svg width={W} height={H} style={{ position:'absolute', inset:0 }} overflow="visible">
        <defs>
          <filter id={`${uid}-g1`}>
            <feGaussianBlur stdDeviation="4" result="blur"/>
            <feMerge><feMergeNode in="blur"/><feMergeNode in="SourceGraphic"/></feMerge>
          </filter>
          <filter id={`${uid}-g2`}>
            <feGaussianBlur stdDeviation="8"/>
          </filter>
        </defs>
        {/* GPS yderste glow */}
        <path d={gps} stroke="rgba(0,229,255,0.30)" strokeWidth="14" fill="none" strokeLinecap="round"
          filter={`url(#${uid}-g2)`}/>
        {/* GPS indre glow */}
        <path d={gps} stroke="rgba(0,229,255,0.60)" strokeWidth="4" fill="none" strokeLinecap="round"
          filter={`url(#${uid}-g1)`}/>
        {/* GPS core */}
        <path d={gps} stroke="#00E5FF" strokeWidth="2" fill="none" strokeLinecap="round"/>

        {/* Pins — neon orbs */}
        {DEMO.map((ms, i) => {
          if (ms.type === 'flag') return null;
          const mx = Math.round(ms.progress * W);
          const my = gY(mx, W, H);
          const col = TYPE_COLOR[ms.type] || '#00E5FF';
          const stemH = 30 + (i % 4) * 14;
          const py = my - stemH - 12;
          return (
            <g key={i}>
              <line x1={mx} y1={my} x2={mx} y2={py + 12}
                stroke={col} strokeWidth="1" opacity="0.5" strokeDasharray="3 3"/>
              {/* Glow bag badge */}
              <circle cx={mx} cy={py + 6} r="14"
                fill={col} opacity="0.08" filter={`url(#${uid}-g2)`}/>
              {/* Badge */}
              <rect x={mx - 16} y={py} width="32" height="12" rx="6"
                fill="rgba(3,3,15,0.88)" stroke={col} strokeWidth="1.2"/>
              <text x={mx} y={py + 8.5} textAnchor="middle"
                fill={col} fontSize="6.5" fontWeight="900" letterSpacing=".07em"
                fontFamily="monospace">{TYPE_LABEL[ms.type]}</text>
              <circle cx={mx} cy={my} r="3.5" fill={col} filter={`url(#${uid}-g1)`}/>
            </g>
          );
        })}
        {/* Målflag */}
        {(() => {
          const fx = Math.round(0.97 * W);
          const fy = gY(fx, W, H);
          return (
            <g>
              <line x1={fx} y1={fy} x2={fx} y2={fy-22} stroke="#fff" strokeWidth="1.5" strokeLinecap="round"/>
              {[0,1,2,3].map(c => [0,1,2].map(r => (
                <rect key={`${c}-${r}`} x={fx+c*5} y={fy-22+r*5} width="5" height="5"
                  fill={(c+r)%2===0 ? 'rgba(255,255,255,0.96)' : 'rgba(3,3,15,0.97)'}/>
              )))}
              <circle cx={fx} cy={fy} r="3" fill="#fff"/>
            </g>
          );
        })()}
      </svg>
      {/* Branding */}
      <div style={{ position:'absolute', bottom:7, right:10, fontSize:10, fontWeight:900,
        color:'rgba(0,229,255,0.8)', letterSpacing:'.14em', textShadow:'0 0 8px rgba(0,229,255,0.5)', fontFamily:'monospace' }}>
        BAREMC
      </div>
    </div>
  );
}

// ─── Design-wrapper med resize ──────────────────────────────────────────────
const DESIGNS_META = [
  { id: 1, name: 'PANORAMA',   sub: 'Lys natur — GPS + elegante badges', Comp: Design1 },
  { id: 2, name: 'FILM NOIR',  sub: 'Cinematisk filmstrimmel — amber GPS', Comp: Design2 },
  { id: 3, name: 'HORIZON',    sub: 'Himmel møder jord — hvid GPS-sti', Comp: Design3 },
  { id: 4, name: 'ATLAS',      sub: 'Topografisk kort — rød stiplet rute', Comp: Design4 },
  { id: 5, name: 'CYBER ROAD', sub: 'Nat + neon + speed — cyan GPS', Comp: Design5 },
];

function DesignCard({ meta, chosen, onChoose }) {
  const ref = useRef(null);
  const [W, setW] = useState(358);
  useEffect(() => {
    if (!ref.current) return;
    const ro = new ResizeObserver(([e]) => setW(Math.round(e.contentRect.width)));
    ro.observe(ref.current);
    return () => ro.disconnect();
  }, []);
  const { Comp } = meta;
  return (
    <div className="sc-card" style={{
      border: chosen ? '2px solid #2DFF88' : '2px solid transparent',
      transition: 'border-color 200ms ease',
    }}>
      <div className="sc-card-head">
        <div>
          <div className="sc-card-name">{meta.id}. {meta.name}</div>
          <div className="sc-card-sub">{meta.sub}</div>
        </div>
        <button
          className="sc-pick"
          onClick={() => onChoose(meta.id)}
          style={{ background: chosen ? '#2DFF88' : 'rgba(45,255,136,0.15)', color: chosen ? '#000' : '#2DFF88' }}
        >
          {chosen ? 'VALGT' : 'VÆLG'}
        </button>
      </div>
      <div ref={ref}>
        <Comp W={W} />
      </div>
    </div>
  );
}

// ─── Hoved-siden ────────────────────────────────────────────────────────────
export default function TimelineShowcasePage() {
  const [chosen, setChosen] = useState(null);
  return (
    <>
      <style>{CSS}</style>
      <div className="sc-wrap">
        <div className="sc-hero">
          <h1>TIDSLINJE — VÆLG DIT DESIGN</h1>
          <p>5 unikke premium-visioner. Tryk VÆLG for at markere den du ønsker.</p>
        </div>

        {DESIGNS_META.map(meta => (
          <DesignCard key={meta.id} meta={meta} chosen={chosen === meta.id} onChoose={setChosen} />
        ))}

        {chosen && (
          <div style={{
            margin:'0 16px', padding:'18px 20px', borderRadius:12,
            background:'rgba(45,255,136,0.12)', border:'1px solid rgba(45,255,136,0.35)',
            textAlign:'center',
          }}>
            <div style={{ color:'#2DFF88', fontWeight:900, fontSize:15, letterSpacing:'.06em', marginBottom:4 }}>
              DESIGN {chosen} — {DESIGNS_META[chosen-1].name} ER VALGT
            </div>
            <div style={{ color:'rgba(255,255,255,0.55)', fontSize:12 }}>
              Sig til mig hvilket nummer du vil have og jeg bygger det direkte ind i din app.
            </div>
          </div>
        )}
      </div>
    </>
  );
}
